/**
 * AWS Cognito Authentication Service
 * Handles user authentication using AWS Amplify
 */

import { Amplify } from 'aws-amplify';
import {
  signIn,
  signUp,
  signOut,
  confirmSignUp,
  getCurrentUser,
  fetchAuthSession,
  resendSignUpCode
} from '@aws-amplify/auth';

// Configure Amplify with Cognito settings
const cognitoConfig = {
  Auth: {
    Cognito: {
      userPoolId: process.env.REACT_APP_COGNITO_USER_POOL_ID || 'us-east-1_8r0OM2Qjb',
      userPoolClientId: process.env.REACT_APP_COGNITO_CLIENT_ID || 'a626kvav0h4t8e21prujrgo95',
    }
  }
};

// Only configure if not already configured
try {
  Amplify.configure(cognitoConfig);
} catch (e) {
  console.log('Amplify already configured');
}

export const authService = {
  /**
   * Sign up a new user
   * @param {string} email - User's email
   * @param {string} password - User's password
   * @returns {Object} Result with success status and data/error
   */
  async signUp(email, password) {
    try {
      const result = await signUp({
        username: email,
        password,
        options: {
          userAttributes: {
            email
          }
        }
      });
      return {
        success: true,
        result,
        needsConfirmation: result.nextStep?.signUpStep === 'CONFIRM_SIGN_UP'
      };
    } catch (error) {
      console.error('Sign up error:', error);
      return {
        success: false,
        error: error.message || 'Failed to sign up'
      };
    }
  },

  /**
   * Confirm sign up with verification code
   * @param {string} email - User's email
   * @param {string} code - Verification code
   * @returns {Object} Result with success status
   */
  async confirmSignUp(email, code) {
    try {
      await confirmSignUp({
        username: email,
        confirmationCode: code
      });
      return { success: true };
    } catch (error) {
      console.error('Confirm sign up error:', error);
      return {
        success: false,
        error: error.message || 'Failed to confirm sign up'
      };
    }
  },

  /**
   * Resend verification code
   * @param {string} email - User's email
   * @returns {Object} Result with success status
   */
  async resendCode(email) {
    try {
      await resendSignUpCode({ username: email });
      return { success: true };
    } catch (error) {
      console.error('Resend code error:', error);
      return {
        success: false,
        error: error.message || 'Failed to resend code'
      };
    }
  },

  /**
   * Sign in a user
   * @param {string} email - User's email
   * @param {string} password - User's password
   * @returns {Object} Result with success status and user data
   */
  async signIn(email, password) {
    try {
      const result = await signIn({
        username: email,
        password
      });
      return {
        success: true,
        result
      };
    } catch (error) {
      console.error('Sign in error:', error);

      // Handle specific error cases
      if (error.name === 'UserNotConfirmedException') {
        return {
          success: false,
          error: 'Please verify your email first',
          needsConfirmation: true
        };
      }

      return {
        success: false,
        error: error.message || 'Failed to sign in'
      };
    }
  },

  /**
   * Sign out the current user
   * @returns {Object} Result with success status
   */
  async signOut() {
    try {
      await signOut();
      return { success: true };
    } catch (error) {
      console.error('Sign out error:', error);
      return {
        success: false,
        error: error.message || 'Failed to sign out'
      };
    }
  },

  /**
   * Get the current authenticated user
   * @returns {Object} Result with user data or null
   */
  async getCurrentUser() {
    try {
      const user = await getCurrentUser();
      return {
        success: true,
        user
      };
    } catch (error) {
      // User is not authenticated - this is not an error
      return {
        success: false,
        user: null
      };
    }
  },

  /**
   * Get the current auth token for API requests
   * @returns {string|null} JWT token or null if not authenticated
   */
  async getAuthToken() {
    try {
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();
      return token || null;
    } catch (error) {
      console.error('Get auth token error:', error);
      return null;
    }
  },

  /**
   * Check if user is currently authenticated
   * @returns {boolean} True if authenticated
   */
  async isAuthenticated() {
    try {
      await getCurrentUser();
      return true;
    } catch {
      return false;
    }
  },

  /**
   * Get user's email from current session
   * @returns {string|null} User's email or null
   */
  async getUserEmail() {
    try {
      const user = await getCurrentUser();
      return user.signInDetails?.loginId || user.username || null;
    } catch {
      return null;
    }
  }
};

export default authService;
